// src/context/ThemeContext.js
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { darkTheme, lightTheme } from "../theme";

const ThemeContext = createContext({
  mode: "dark",
  toggleTheme: () => {},
});

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    try {
      const savedMode = localStorage.getItem("themeMode");
      return savedMode === "light" ? "light" : "dark";
    } catch (e) {
      return "dark";
    }
  });

  const toggleTheme = () => {
    setMode((prevMode) => {
      const nextMode = prevMode === "dark" ? "light" : "dark";
      try {
        localStorage.setItem("themeMode", nextMode);
      } catch (e) {
        // Silently catch localStorage errors in isolated/incognito environments
      }
      return nextMode;
    });
  };

  const activeTheme = useMemo(() => {
    return mode === "dark" ? darkTheme : lightTheme;
  }, [mode]);

  // Sync mode changes with CSS variables on the document element
  useEffect(() => {
    const root = document.documentElement;
    const palette = activeTheme.palette;

    const colorsToMap = {
      primary: palette.primary.main,
      onPrimary: palette.primary.contrastText,
      primaryContainer: palette.primaryContainer,
      onPrimaryContainer: palette.onPrimaryContainer,
      secondary: palette.secondary.main,
      onSecondary: palette.secondary.contrastText,
      secondaryContainer: palette.secondaryContainer,
      onSecondaryContainer: palette.onSecondaryContainer,
      tertiary: palette.tertiary,
      onTertiary: palette.onTertiary,
      tertiaryContainer: palette.tertiaryContainer,
      onTertiaryContainer: palette.onTertiaryContainer,
      background: palette.background.default,
      onBackground: palette.text.primary,
      surface: palette.surface,
      onSurface: palette.onSurface,
      surfaceVariant: palette.surfaceVariant,
      onSurfaceVariant: palette.onSurfaceVariant,
      outline: palette.outline,
      outlineVariant: palette.outlineVariant,
      surfaceContainerLowest: palette.surfaceContainerLowest,
      surfaceContainerLow: palette.surfaceContainerLow,
      surfaceContainer: palette.surfaceContainer,
      surfaceContainerHigh: palette.surfaceContainerHigh,
      surfaceContainerHighest: palette.surfaceContainerHighest,
    };

    Object.entries(colorsToMap).forEach(([key, value]) => {
      if (value) {
        const kebabKey = key
          .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
          .toLowerCase();
        root.style.setProperty(`--md-sys-color-${kebabKey}`, value);
      }
    });

    // Expose shapes as well
    const shapesToMap = {
      small: `${activeTheme.shape.small}px`,
      medium: `${activeTheme.shape.medium}px`,
      large: `${activeTheme.shape.large}px`,
      extraLarge: `${activeTheme.shape.extraLarge}px`,
      full: `${activeTheme.shape.full}px`,
    };

    Object.entries(shapesToMap).forEach(([key, value]) => {
      const kebabKey = key.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
      root.style.setProperty(`--md-sys-shape-${kebabKey}`, value);
    });

    if (mode === "dark") {
      root.classList.add("dark");
      root.classList.remove("light");
    } else {
      root.classList.add("light");
      root.classList.remove("dark");
    }
  }, [mode, activeTheme]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={activeTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeToggle = () => useContext(ThemeContext);
